import { useState, type PointerEvent } from "react";
import { useApi } from "../api/useApi";
import { holdingsBenchmarkInput } from "../lib/portfolioBenchmarks";
import type {
  BenchmarkMetrics, BenchmarkPeriod, BrokerageHolding,
  PortfolioBenchmarkInput, PortfolioBenchmarks as Comparison,
} from "../types";
import { changeColor, money, signedPercent } from "../lib/format";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const COLORS = ["#80bfff", "#67c58c", "#b69aeb", "#e0ba54", "#e48080", "#65c5bf", "#dd9858", "#a6afc4"];
const PERIODS: Array<[BenchmarkPeriod, string]> = [["ytd", "YTD"], ["1y", "1Y"], ["3y", "3Y"], ["5y", "5Y"], ["10y", "10Y"]];

function GrowthChart({ data, selected }: { data: Comparison; selected: string[] }) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const curves = [
    ...(data.portfolio ? [{ name: "Current holdings", color: COLORS[0], points: data.portfolio.points }] : []),
    ...data.benchmarks.flatMap((row, index) =>
      row.available && selected.includes(row.symbol) && row.points
        ? [{ name: row.name, color: COLORS[index + 1], points: row.points }] : []),
  ];
  const dates = curves[0]?.points ?? [];
  if (dates.length < 2) return null;
  const width = 800, height = 270, left = 70, right = 12, top = 15, bottom = 30;
  const timestamps = dates.map((point) => Date.parse(point.date));
  const first = timestamps[0], last = timestamps.at(-1)!;
  const values = curves.flatMap((curve) => curve.points.map((point) => point.value));
  const minimum = Math.min(100, ...values), maximum = Math.max(100, ...values);
  const padding = Math.max(2, (maximum - minimum) * 0.08);
  const low = minimum - padding, high = maximum + padding;
  const x = (time: number) => left + (time - first) / (last - first) * (width - left - right);
  const y = (value: number) => height - bottom - (value - low) / (high - low) * (height - top - bottom);
  const index = Math.min(activeIndex ?? dates.length - 1, dates.length - 1);
  const inspect = (event: PointerEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return;
    const time = first + ((event.clientX - rect.left) / rect.width * width - left) /
      (width - left - right) * (last - first);
    let nearest = 0;
    timestamps.forEach((stamp, i) => {
      if (Math.abs(stamp - time) < Math.abs(timestamps[nearest] - time)) nearest = i;
    });
    setActiveIndex(nearest);
  };
  return (
    <div className="space-y-3">
      <div className="text-sm font-medium">Growth of $10,000</div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full cursor-crosshair" role="img"
        aria-label="Hypothetical current holdings and benchmark growth over matching dates"
        onPointerMove={inspect} onPointerDown={inspect}>
        {[0, 1, 2, 3].map((tick) => {
          const value = low + (high - low) * tick / 3;
          return <g key={tick}>
            <line x1={left} x2={width - right} y1={y(value)} y2={y(value)} stroke="var(--border)" />
            <text x={left - 8} y={y(value) + 4} textAnchor="end" fontSize="11" fill="var(--muted-foreground)">{money(value * 100)}</text>
          </g>;
        })}
        <line x1={left} x2={width - right} y1={y(100)} y2={y(100)} stroke="var(--muted-foreground)" strokeDasharray="4 4" />
        {curves.map((curve) => <path key={curve.name} fill="none" stroke={curve.color}
          strokeWidth={curve.name === "Current holdings" ? 3 : 1.7}
          d={curve.points.map((point, i) => `${i === 0 ? "M" : "L"}${x(Date.parse(point.date))},${y(point.value)}`).join(" ")} />)}
        <line x1={x(timestamps[index])} x2={x(timestamps[index])} y1={top} y2={height - bottom}
          stroke="var(--muted-foreground)" strokeDasharray="3 3" />
        <text x={left} y={height - 5} fontSize="11" fill="var(--muted-foreground)">{data.start_date}</text>
        <text x={width - right} y={height - 5} textAnchor="end" fontSize="11" fill="var(--muted-foreground)">{data.end_date}</text>
      </svg>
      <input type="range" min={0} max={dates.length - 1} value={index}
        onChange={(event) => setActiveIndex(Number(event.target.value))}
        className="w-full accent-primary" aria-label="Inspect comparison date" />
      <div className="text-xs text-muted-foreground" aria-live="polite">{dates[index].date}</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs sm:grid-cols-3 lg:grid-cols-4">
        {curves.map((curve) => <div key={curve.name} className="flex items-center gap-2">
          <span className="size-2 shrink-0 rounded-full" style={{ background: curve.color }} />
          <span>{curve.name} <span className="tabular-nums text-muted-foreground">{money(curve.points[index].value * 100)}</span></span>
        </div>)}
      </div>
    </div>
  );
}

function BenchmarkComparison({ input }: { input: ReturnType<typeof holdingsBenchmarkInput> }) {
  const api = useApi();
  const [period, setPeriod] = useState<BenchmarkPeriod>("1y");
  const [data, setData] = useState<Comparison | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState(["SPY", "QQQ", "GLD", "DIA"]);
  const load = async (nextPeriod: BenchmarkPeriod) => {
    setPeriod(nextPeriod);
    setLoading(true);
    setError(null);
    // Clear the previous window so an error cannot leave a misleading period label.
    setData(null);
    try {
      const request: PortfolioBenchmarkInput = { positions: input.positions,
        cash_weight: input.cash_weight, unpriced_weight: input.unpriced_weight, period: nextPeriod };
      setData(await api.getPortfolioBenchmarks(request));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to load benchmark comparison.");
    } finally { setLoading(false); }
  };
  const disabled = input.invalid || input.positions.length > 50 ||
    (input.positions.length === 0 && input.cash_weight === 0);
  const metricCells = (metrics: Partial<BenchmarkMetrics> | null) => <>
    <TableCell className={`text-right tabular-nums ${changeColor(metrics?.total_return_percent)}`}>{signedPercent(metrics?.total_return_percent)}</TableCell>
    <TableCell className={`text-right tabular-nums ${changeColor(metrics?.cagr_percent)}`}>{signedPercent(metrics?.cagr_percent)}</TableCell>
    <TableCell className="text-right tabular-nums text-destructive">{signedPercent(metrics?.max_drawdown_percent)}</TableCell>
    <TableCell className="text-right tabular-nums">{money(metrics?.ending_value)}</TableCell>
  </>;
  return (
    <section className="space-y-4 rounded-md border border-border p-4" data-testid="portfolio-benchmarks">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">Portfolio vs. benchmarks</h3>
          <p className="mt-1 text-xs text-muted-foreground">Hypothetical performance of today’s holdings, starting at today’s weights.</p>
        </div>
        <Button type="button" variant="outline" size="sm" disabled={disabled || loading} onClick={() => void load(period)}>
          {loading ? "Comparing…" : data ? "Refresh comparison" : "Compare benchmarks"}
        </Button>
      </div>
      <div className="flex flex-wrap gap-2" aria-label="Benchmark comparison period">
        {PERIODS.map(([key, label]) => <Button key={key} type="button" size="sm"
          variant={period === key ? "secondary" : "ghost"} aria-pressed={period === key}
          disabled={disabled || loading} onClick={() => { if (data || error) void load(key); else setPeriod(key); }}>{label}</Button>)}
      </div>
      {disabled && <p className="text-sm text-muted-foreground">Comparison needs up to 50 priced holdings in USD with non-negative, known position values. Foreign-currency, short, or missing-value positions need to be resolved first.</p>}
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      {loading && <p role="status" className="text-sm text-muted-foreground">Loading holdings and benchmark history…</p>}
      {data && <>
        <p className="text-xs text-muted-foreground">
          {data.start_date} to {data.end_date} · {data.coverage_percent.toFixed(2)}% of holdings value analyzed.
          {data.excluded_symbols.length > 0 && ` Excluded for missing history: ${data.excluded_symbols.join(", ")}.`}
          {data.unpriced_percent > 0 && ` Unpriced holdings: ${data.unpriced_percent.toFixed(2)}%.`}
          {data.cash_percent > 0 && ` Cash: ${data.cash_percent.toFixed(2)}%, earning 0%.`}
          {data.coverage_percent < 99.995 && " Covered positions are rescaled to 100%; results represent only that portion."}
        </p>
        {!data.portfolio && <p className="text-sm text-muted-foreground">No portfolio holdings have enough history for this period. Choose a shorter window.</p>}
        <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs" aria-label="Chart benchmarks">
          {data.benchmarks.map((row) => <label key={row.symbol} className="flex items-center gap-1.5">
            <input type="checkbox" className="accent-primary" checked={selected.includes(row.symbol)} disabled={!row.available}
              onChange={() => setSelected((current) => current.includes(row.symbol) ? current.filter((symbol) => symbol !== row.symbol) : [...current, row.symbol])} />
            {row.name} ({row.symbol})
          </label>)}
        </div>
        <GrowthChart key={`${data.start_date}-${data.end_date}`} data={data} selected={selected} />
        <Table>
          <TableHeader><TableRow>
            <TableHead>Investment</TableHead><TableHead className="text-right">Total return</TableHead>
            <TableHead className="text-right">CAGR</TableHead><TableHead className="text-right">Max drawdown</TableHead>
            <TableHead className="text-right">$10,000 becomes</TableHead>
            <TableHead className="text-right" title="Portfolio total return minus benchmark total return, in percentage points">Portfolio lead (pp)</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            <TableRow className="bg-primary/5 font-medium"><TableCell>Current holdings</TableCell>{metricCells(data.portfolio)}<TableCell className="text-right">—</TableCell></TableRow>
            {data.benchmarks.map((row) => <TableRow key={row.symbol}>
              <TableCell>{row.name} <span className="text-xs text-muted-foreground">{row.symbol}{!row.available && " · unavailable"}</span></TableCell>
              {metricCells(row)}
              <TableCell className={`text-right tabular-nums ${changeColor(row.portfolio_excess_percent)}`}>
                {row.portfolio_excess_percent == null ? "—" : `${row.portfolio_excess_percent > 0 ? "+" : ""}${row.portfolio_excess_percent.toFixed(2)}`}
              </TableCell>
            </TableRow>)}
          </TableBody>
        </Table>
        <p className="text-xs text-muted-foreground">{data.method} This is a backtest of the covered holdings, not your actual account return. Benchmarks are USD ETF proxies, not the indexes or spot gold directly. Positive portfolio lead means the holdings outperformed over these dates. YTD is not annualized.</p>
      </>}
    </section>
  );
}

export function PortfolioBenchmarks({ holdings }: { holdings: BrokerageHolding[] }) {
  const input = holdingsBenchmarkInput(holdings);
  return <BenchmarkComparison key={JSON.stringify(input)} input={input} />;
}
